/**
 * SchemaReviewStep - Review and edit inferred schema fields
 *
 * Allows users to review inferred schema fields in an editable DataTable.
 * Supports adding, removing, and modifying fields before schema creation.
 */

import React, { useMemo, useCallback } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import { useFieldEditor } from '@/features/schemas/store/schemaCreationStore';
import { validateFields, detectPIIFields } from '@/features/schemas/utils/schemaValidation';
import type { SchemaField, FieldType } from '@/features/schemas/types/schema.types';

/**
 * Available field types for dropdown
 */
const FIELD_TYPES: FieldType[] = [
  'string',
  'integer',
  'bigint',
  'float',
  'double',
  'boolean',
  'timestamp',
  'date',
  'binary',
  'array',
  'map',
  'struct',
];

/**
 * SchemaReviewStep Component
 *
 * Displays inferred schema fields in an editable DataTable.
 * Users can modify field properties, add new fields, or remove fields.
 */
export const SchemaReviewStep: React.FC = () => {
  const { fields, addField, removeField, updateField } = useFieldEditor();

  const piiFields = useMemo(() => detectPIIFields(fields), [fields]);
  const validationErrors = useMemo(() => validateFields(fields), [fields]);

  const handleAddField = useCallback(() => {
    const newField: SchemaField = {
      name: `field_${fields.length + 1}`,
      type: 'string',
      nullable: false,
      description: '',
      isPII: false,
    };
    addField(newField);
  }, [addField, fields.length]);

  const handleRemoveField = useCallback(
    (index: number) => {
      removeField(index);
    },
    [removeField]
  );

  const columns = useMemo<ColumnDef<SchemaField>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Field Name',
        cell: ({ row }) => (
          <Input
            value={row.original.name ?? ''}
            onChange={(e) => updateField(row.index, { name: e.target.value })}
            className="h-8 font-medium"
          />
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Select
            value={row.original.type ?? ''}
            onValueChange={(value) => updateField(row.index, { type: value as FieldType })}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        accessorKey: 'nullable',
        header: 'Nullable',
        cell: ({ row }) => (
          <Select
            value={row.original.nullable ? 'Yes' : 'No'}
            onValueChange={(value) => updateField(row.index, { nullable: value === 'Yes' })}
          >
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
            </SelectContent>
          </Select>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <Input
            value={row.original.description ?? ''}
            onChange={(e) => updateField(row.index, { description: e.target.value })}
            className="h-8"
          />
        ),
      },
      {
        accessorKey: 'isPII',
        header: 'PII',
        cell: ({ row }) => {
          const isPII = (row.original as any).isPII === true;
          return (
            <div className="flex items-center gap-2">
              <Select
                value={isPII ? 'Yes' : 'No'}
                onValueChange={(value) => updateField(row.index, { isPII: value === 'Yes' })}
              >
                <SelectTrigger className="h-8 w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {isPII && (
                <Badge variant="destructive" className="text-xs">
                  PII
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRemoveField(row.index)}
            className="h-8 w-8 p-0"
            aria-label="Remove field"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        ),
      },
    ],
    [updateField, handleRemoveField]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Review Schema Fields</CardTitle>
          <CardDescription>
            Review and modify the inferred schema fields. Edit any cell to update.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* PII Warning */}
          {piiFields.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>PII Detected:</strong> The following fields may contain personally
                identifiable information: <strong>{piiFields.join(', ')}</strong>. Consider
                implementing appropriate data protection measures.
              </AlertDescription>
            </Alert>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Validation Errors:</strong>
                <ul className="list-disc list-inside mt-2">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Add Field Button */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {fields.length} field{fields.length !== 1 ? 's' : ''} defined
            </p>
            <Button onClick={handleAddField} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Field
            </Button>
          </div>

          {/* Fields DataTable */}
          <DataTable data={fields} columns={columns} pageSize={50} />

          {/* Instructions */}
          <div className="pt-4 space-y-2 text-sm text-muted-foreground">
            <p className="font-medium">Tips:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Edit any cell to update field properties</li>
              <li>Use the dropdown to change field types and nullable settings</li>
              <li>Mark fields as PII via the PII column dropdown</li>
              <li>Add descriptions to document field purpose</li>
              <li>Remove unnecessary fields using the delete button</li>
              <li>Fields marked PII are tagged with a badge and require special handling</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
